import { type TouchlessTicket, type TouchlessTicketSaveRequest, type RequestTicketSaveRequest, type VehicleInfo } from '@parkingbase/ecommerce-api-demo';
import { create } from 'zustand';

import type { RawAxiosRequestConfig } from 'axios';
import { TouchlessService } from '@/services/touchless.service';
import { logger } from '@/lib/logger';
import {
  extractApiErrorMessage,
  getApiErrorStatus,
  readLocationClosedRefusal,
} from '@/lib/api-error-utils';

const touchlessService = new TouchlessService();

/**
 * Why the last ticket fetch failed, as a machine code rather than a sentence.
 *
 * `timeout` covers every case where no HTTP response arrived at all — the axios
 * timeout firing (`DEFAULT_API_TIMEOUT_MS`), and outright network failure. Both look identical to the caller and both are
 * worth retrying, so they share one reason.
 *
 * ## Invariants:
 * - A code, never user-facing prose. Why: this store has no access to `next-intl`, so a sentence
 *   set here can only ever be untranslated English. The consuming page maps the code to a
 *   localized message (PB-4915).
 */
export type TouchlessFetchFailureReason = 'timeout' | 'notFound' | 'server';

/**
 * Whether a failed ticket fetch is worth offering a retry for.
 *
 * A `notFound` is terminal — the ticket id is wrong, so retrying re-renders the same dead end.
 * Everything else (timeout, 5xx, any other status) can succeed on a second attempt.
 */
export function isRetryableFetchFailure(reason: TouchlessFetchFailureReason | null): boolean {
  return reason !== null && reason !== 'notFound';
}

/** Classifies a caught ticket-fetch error into a {@link TouchlessFetchFailureReason}. */
function classifyFetchFailure(error: unknown): TouchlessFetchFailureReason {
  const status = getApiErrorStatus(error);
  if (status === undefined) return 'timeout';
  if (status === 404) return 'notFound';
  return 'server';
}

interface TouchlessState {
  /** Current touchless ticket data */
  ticket: TouchlessTicket | null;
  /** Loading state */
  isLoading: boolean;
  /** Loading state for pickup request */
  isLoadingPickup: boolean;
  /** Loading state for receipt download */
  isLoadingReceipt: boolean;
  /** Loading state for vehicle update */
  isUpdatingVehicle: boolean;
  /** Error message */
  error: string | null;
  /** Why the last ticket fetch failed, or null. Consumers localize this; see the type's docblock. */
  fetchFailureReason: TouchlessFetchFailureReason | null;
  /**
   * The server's own sentence for why the last {@link TouchlessState.submitPayment} was refused,
   * or null when it succeeded or the refusal carried no such sentence.
   *
   * ## Invariants:
   * - Set only for a closed-location refusal (PB-7734 `GARAGE_CLOSED`, an extension at a location
   *   that is inactive or under a business closure). Why: `error` stays the fixed
   *   `'Payment failed'` every other refusal has always produced, so a caller that reads only
   *   `error` behaves exactly as before; a caller that wants the reason opts in by reading this.
   * - Cleared at the start of every submit. Why: a stale sentence from an earlier attempt would
   *   otherwise explain a later, unrelated failure.
   */
  paymentRefusal: string | null;

  /** Fetch touchless ticket by ID */
  fetchTicket: (
    ticketId: string,
    endTime?: string,
    companyId?: string,
    options?: RawAxiosRequestConfig
  ) => Promise<TouchlessTicket | null>;

  /**
   * Records a ticket-fetch failure that happened outside {@link TouchlessState.fetchTicket}.
   *
   * Needed because the fetch can fail to even start: the page defers it until reCAPTCHA hands
   * over `executeRecaptcha`, and if that never arrives there is no request to catch — the page
   * would otherwise sit on its loader forever with no error and no way out (PB-4915).
   */
  setFetchFailure: (reason: TouchlessFetchFailureReason | null) => void;

  /** Submit touchless payment */
  submitPayment: (
    saveRequest: TouchlessTicketSaveRequest,
    options?: RawAxiosRequestConfig
  ) => Promise<TouchlessTicketSaveRequest | null>;

  /** Decode a QR code to extract ticket info */
  decodeQr: (touchlessTicket: TouchlessTicket) => Promise<TouchlessTicketSaveRequest | null>;

  /** Fetch company info from a touchless URL */
  getCompanyInfo: (url: string) => Promise<string | null>;

  /** Request vehicle pickup */
  requestPickup: (request: RequestTicketSaveRequest) => Promise<RequestTicketSaveRequest | null>;

  /** Update vehicle details for a ticket */
  updateVehicleForTicket: (
    company: string,
    ticketId: string,
    vehicleInfo: VehicleInfo,
    options?: RawAxiosRequestConfig
  ) => Promise<boolean>;

  /** Download touchless transaction receipt as PDF blob */
  downloadReceipt: (
    transactionId: string,
    options?: RawAxiosRequestConfig
  ) => Promise<File | null>;

  /** Reset store to initial state */
  reset: () => void;
}

const initialState = {
  ticket: null,
  isLoading: false,
  isLoadingPickup: false,
  isLoadingReceipt: false,
  isUpdatingVehicle: false,
  error: null,
  fetchFailureReason: null,
  paymentRefusal: null,
};

export const useTouchlessStore = create<TouchlessState>((set) => ({
  ...initialState,

  fetchTicket: async (ticketId, endTime?, companyId?, options?) => {
    set({ isLoading: true, error: null, fetchFailureReason: null });
    try {
      const data = await touchlessService.getTicketById(ticketId, endTime, companyId, options);
      set({ ticket: data, isLoading: false, fetchFailureReason: null });
      return data;
    } catch (error) {
      logger.error('Failed to fetch touchless ticket', error, {
        feature: 'touchless-store',
        route: 'fetchTicket',
      });
      const reason = classifyFetchFailure(error);
      set({
        isLoading: false,
        fetchFailureReason: reason,
        // Kept for any consumer reading `error` directly, and so a message the server took care
        // to write is not discarded. The page prefers the localized copy for `fetchFailureReason`.
        error: extractApiErrorMessage(error, 'Failed to fetch ticket'),
      });
      return null;
    }
  },

  setFetchFailure: (reason) =>
    set({ fetchFailureReason: reason, isLoading: false, ...(reason === null ? { error: null } : {}) }),

  submitPayment: async (saveRequest, options?) => {
    set({ isLoading: true, error: null, paymentRefusal: null });
    try {
      const data = await touchlessService.submitPayment(saveRequest, options);
      set({ isLoading: false });
      return data;
    } catch (error) {
      logger.error('Failed to submit touchless payment', error, {
        feature: 'touchless-store',
        route: 'submitPayment',
      });
      set({
        isLoading: false,
        error: 'Payment failed',
        paymentRefusal: readLocationClosedRefusal(error),
      });
      return null;
    }
  },

  decodeQr: async (touchlessTicket) => {
    set({ isLoading: true, error: null });
    try {
      const data = await touchlessService.decodeQrCode(touchlessTicket);
      set({ isLoading: false });
      return data;
    } catch (error) {
      logger.error('Failed to decode QR code', error, {
        feature: 'touchless-store',
        route: 'decodeQr',
      });
      set({ isLoading: false, error: 'Failed to decode QR code' });
      return null;
    }
  },

  getCompanyInfo: async (url) => {
    try {
      const data = await touchlessService.getCompanyInfoFromUrl(url);
      return data;
    } catch (error) {
      logger.error('Failed to get company info', error, {
        feature: 'touchless-store',
        route: 'getCompanyInfo',
      });
      return null;
    }
  },

  requestPickup: async (request) => {
    set({ isLoadingPickup: true, error: null });
    try {
      const data = await touchlessService.requestPickup(request);
      set({ isLoadingPickup: false });
      return data;
    } catch (error) {
      logger.error('Failed to request pickup', error, {
        feature: 'touchless-store',
        route: 'requestPickup',
      });
      set({ isLoadingPickup: false, error: 'Failed to request pickup' });
      return null;
    }
  },

  updateVehicleForTicket: async (company, ticketId, vehicleInfo, options?) => {
    set({ isUpdatingVehicle: true, error: null });
    try {
      const result = await touchlessService.updateVehicleForTicket(
        company,
        ticketId,
        vehicleInfo,
        options
      );
      set({ isUpdatingVehicle: false });
      return result;
    } catch (error) {
      logger.error('Failed to update vehicle for ticket', error, {
        feature: 'touchless-store',
        route: 'updateVehicleForTicket',
      });
      set({ isUpdatingVehicle: false, error: 'Failed to update vehicle' });
      return false;
    }
  },

  downloadReceipt: async (transactionId, options?) => {
    set({ isLoadingReceipt: true, error: null });
    try {
      const data = await touchlessService.downloadReceipt(transactionId, options);
      set({ isLoadingReceipt: false });
      return data;
    } catch (error) {
      logger.error('Failed to download touchless receipt', error, {
        feature: 'touchless-store',
        route: 'downloadReceipt',
      });
      set({ isLoadingReceipt: false, error: 'Failed to download receipt' });
      return null;
    }
  },

  reset: () => set(initialState),
}));
